import './Landingpage.scss';

import { Button, ThemeProvider, createTheme } from '@mui/material';
import { deDE } from '@mui/material/locale';
import { Heart, Mail, ShieldCheck, Sparkles, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import ForgotPasswordModal from 'components/ForgotPasswordModal';
import ForgotPasswordModalx from 'components/ForgotPasswordModalx';
import Config from 'config';
import { FORGOT_PASSWORD_PATH, FORGOT_PASSWORD_PATHH, IMPRESSUM_PATH, REGISTER_USER } from '../../models/Paths';
import { GoogleService } from '../../services/Google/GoogleService';
import useGoogleLoginHandler from '../../services/Google/useGoogleLoginHandler';
import FacebookService from '../../services/Facebook/FacebookService';
import useLoginHandler from '../../services/Facebook/useLoginHandler';
import { DefaultTheme } from '../../theme/DefaultTheme';
import LoginModal from 'components/NewRegister/Login';
import LandingLogo from 'assets/images/logos/logo-with-writing.svg';

export interface ILandingpageProps {}

const previewProfiles = [
    { image: 'https://admin.nurtreffen.de/images/fotos/i80K1IfV_W9JmSQ_srPrgT9dIeHn-_.jpg', label: 'Lia, 32' },
    { image: 'https://admin.nurtreffen.de/images/fotos/3KNsE85AD0G__retOZ_9T_hP8kq__8.jpg', label: 'Valeria, 30' },
    { image: 'https://admin.nurtreffen.de/images/fotos/j_XkY5bg_0ZOT17siE_UU03__HmPHc.jpg', label: 'Doppel-D, 38' },
    { image: 'https://admin.nurtreffen.de/images/fotos/tgxJEHqE4sI6_-MTO_O1BaO1wzZd5_.jpg', label: 'Barbara, 46' },
];

export const Landingpage = React.memo((props: ILandingpageProps) => {
    const history = useHistory();
    const forgotPasswordRoute = useRouteMatch({ path: FORGOT_PASSWORD_PATH, exact: true });
    const forgotPasswordLegacyRoute = useRouteMatch({ path: FORGOT_PASSWORD_PATHH, exact: true });
    const isForgotPasswordOpen = Boolean(forgotPasswordRoute);
    const isSetPasswordOpen = Boolean(forgotPasswordLegacyRoute);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);

    const siteName = useMemo(() => Config.GLOBAL_SITE_NAME || 'Nurtreffen', []);
    const handleFBLogin = useLoginHandler();
    const handleGoogleLogin = useGoogleLoginHandler();

    useEffect(() => {
        FacebookService.init();
        GoogleService.init();
    }, []);

    const openRegister = useCallback(() => {
        history.push(REGISTER_USER);
    }, [history]);

    const loginWithFacebook = useCallback(() => {
        setLoading(true);
        FacebookService.login(async (response) => {
            try {
                if (response.status === 'connected') {
                    await handleFBLogin(response);
                } else {
                    FacebookService.logout();
                }
            } finally {
                setLoading(false);
            }
        });
    }, [handleFBLogin]);

    const loginWithGoogle = useCallback(async () => {
        setLoading(true);
        try {
            await GoogleService.login(handleGoogleLogin);
        } finally {
            setLoading(false);
        }
    }, [handleGoogleLogin]);

    return (
        <ThemeProvider theme={createTheme(DefaultTheme, deDE)}>
            <main className="landing-redesign">
                <header className="landing-nav">
                    <div className="landing-brand-lockup">
                        <Link to="/" className="landing-brand" aria-label={`${siteName} Startseite`}>
                            <img src={LandingLogo} alt={siteName} />
                        </Link>
                    </div>
                    <button type="button" className="landing-login-link" onClick={() => setLoginModalOpen(true)} disabled={isLoading}>
                        Einloggen
                    </button>
                </header>

                <section className="landing-hero" aria-label="Kostenlos anmelden">
                    <div className="landing-story">
                        <div className="landing-visual" aria-hidden="true">
                            <div className="profile-phone">
                                <div className="phone-notch" />
                                <div className="profile-photo" style={{ '--profile-photo-url': `url(${previewProfiles[0].image})` } as React.CSSProperties} />
                                <div className="profile-card-copy">
                                    <strong>Lia, 32</strong>
                                    <span>Hamburg - 3 km entfernt</span>
                                </div>
                                <div className="profile-actions">
                                    <span className="action-circle action-neutral">
                                        <X size={20} strokeWidth={1.8} />
                                    </span>
                                    <span className="action-circle action-like">
                                        <Heart size={16} fill="currentColor" strokeWidth={2} />
                                    </span>
                                </div>
                            </div>

                            <div className="chat-phone">
                                <div className="phone-notch" />
                                <div className="chat-header">
                                    <strong>Felix</strong>
                                    <span>Online</span>
                                </div>
                                <div className="message message-in">Hey! Dein Profil hat mich wirklich angesprochen 😊</div>
                                <div className="message message-out">Hallo! Das freut mich sehr!</div>
                                <div className="message message-in">Hättest du Lust auf einen Kaffee diese Woche?</div>
                                <div className="message message-out">Sehr gerne, wann passt es dir?</div>
                                <div className="chat-input">Nachricht schreiben...</div>
                            </div>

                            <div className="profile-strip">
                                {previewProfiles.slice(0, 4).map((profile) => (
                                    <div className="profile-strip-card" key={profile.image}>
                                        <img src={profile.image} alt="" loading="lazy" />
                                        <strong>{profile.label}</strong>
                                    </div>
                                ))}
                                <small>Heute neu auf {siteName}</small>
                            </div>

                            <blockquote className="love-note" id="erfolgsgeschichten">
                                <p>
                                    "Nach drei Tagen auf {siteName} haben wir uns das erste Mal getroffen - heute sind wir unzertrennlich."
                                </p>
                                <cite>Markus & Jana · Berlin</cite>
                            </blockquote>
                        </div>

                        <div className="hero-copy" id="ueber-uns">
                            <div className="eyebrow">
                                <span />
                                Jeden Tag neue Treffen, neue Mitglieder!
                            </div>
                            <h1>
                                Wo echte Begegnungen <em>beginnen.</em>
                            </h1>
                            <p>
                                {siteName} bringt dich mit Menschen zusammen, mit denen es wirklich passt - für ein Date, eine Freundschaft oder die
                                große Liebe.
                            </p>
                            <div className="hero-stats" aria-label="Kennzahlen">
                                <div>
                                    <strong>2,3M+</strong>
                                    <span>Glückliche Paare</span>
                                </div>
                                <div>
                                    <strong>4,9 ★</strong>
                                    <span>App-Bewertung</span>
                                </div>
                                <div>
                                    <strong>180+</strong>
                                    <span>Länder</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <aside className="signup-panel">
                        <div className="signup-content">
                            <h2>Jetzt kostenlos anmelden</h2>
                            <p>Dein nächstes Abenteuer wartet auf dich</p>

                            <Button className="signup-button email-button" onClick={openRegister} disabled={isLoading} fullWidth>
                                <Mail size={18} />
                                Weiter mit E-Mail
                            </Button>

                            <div className="divider">
                                <span />
                                <strong>ODER</strong>
                                <span />
                            </div>

                            <Button className="signup-button google-button" onClick={loginWithGoogle} disabled={isLoading} fullWidth>
                                <span className="google-mark">G</span>
                                Mit Google anmelden
                            </Button>

                            <Button className="signup-button facebook-button" onClick={loginWithFacebook} disabled={isLoading} fullWidth>
                                <span className="facebook-mark">f</span>
                                Mit Facebook anmelden
                            </Button>

                            <Button className="signup-button login-button" onClick={() => setLoginModalOpen(true)} disabled={isLoading} fullWidth>
                                Schon Mitglied? Hier einloggen
                            </Button>

                            <p className="terms-copy">
                                Mit der Registrierung stimmst du unseren <Link to="/agb">Nutzungsbedingungen</Link> sowie der{' '}
                                <Link to="/datenschutz">Datenschutzerklärung</Link> zu. <Link to={IMPRESSUM_PATH}>Impressum</Link>.
                            </p>

                            <div className="trust-row">
                                <div>
                                    <ShieldCheck size={17} />
                                    <span>Verifizierte Profile & diskrete Nutzung</span>
                                </div>
                                <div>
                                    <Sparkles size={17} />
                                    <span>Kostenlos starten, kein Abo nötig</span>
                                </div>
                                <div>
                                    <Heart size={17} />
                                    <span>100 % made & gehostet in Deutschland</span>
                                </div>
                            </div>
                        </div>
                    </aside>
                </section>

                <footer className="landing-footer">
                    <nav className="landing-footer__links" aria-label="Rechtliches">
                        <Link to="/support">Kontakt</Link>
                        <Link to={IMPRESSUM_PATH}>Impressum</Link>
                        <Link to="/datenschutz">Datenschutz</Link>
                        <Link to="/agb">AGB</Link>
                        <Link to="/content-guidelines">Content &amp; Community Guidelines</Link>
                    </nav>
                    <p className="landing-footer__legal">
                        Diese Webseite darf ausschlie&szlig;lich von Personen genutzt werden, die mindestens 18 Jahre alt sind. Die Plattform stellt
                        einen virtuellen Chat-Dienst f&uuml;r unterhaltsame, flirtende und fantasievolle Gespr&auml;che zur Verf&uuml;gung. Hierbei
                        werden fiktive Profile verwendet, mit denen keine pers&ouml;nlichen oder realen Treffen m&ouml;glich sind. Die Kosten je
                        Nachricht k&ouml;nnen je nach gew&auml;hlter Zahlungsart unterschiedlich ausfallen. Bitte informieren Sie sich vor der Nutzung
                        der Webseite &uuml;ber die geltenden Nutzungsbedingungen und Datenschutzbestimmungen.
                    </p>
                </footer>

                <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
                <ForgotPasswordModal open={isForgotPasswordOpen} />
                {isSetPasswordOpen && <ForgotPasswordModalx />}
            </main>
        </ThemeProvider>
    );
});
